/**
 * Automations Section — settings section for managing CRM automations.
 *
 * Lists automations with create and toggle actions. Clicking a row opens
 * the CRM object slideout for that automation. An "Add" button opens the
 * automation library slideout for template selection.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus } from '@untitledui/icons';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { useAuthInfo } from '@/allauth_auth/hooks.js';
import { useOrg } from '@/contexts/OrgContext';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { usePermissions } from '@/hooks/usePermissions';
import { REMINDER_AUTOMATION_QUERY_KEY } from '@/hooks/useReminderAutomationEnabled';
import { ButtonList } from '@/lib/base/button-list';
import { Button } from '@/lib/base/buttons/button';
import { QueryErrorState } from '@/lib/components/QueryErrorState';
import { BuiltInAutomationLibrary } from '@/lib/crm/automations/BuiltInAutomationLibrary';
import {
  REMINDER_AUTOMATION_SCRIPT_NAME,
  triggerReminderBackfill,
} from '@/lib/crm/automations/reminderBackfill';
import {
  INTERNAL_SCRIPT_NAMES,
  WEBSITE_SCRIPT_NAMES,
} from '@/lib/crm/automations/templates';
import { buildCreateInBuilderNavState } from '@/lib/crm/components/badges/buildBuilderModeNavState';
import { createEventBuilder } from '@/lib/crm/core/EventBuilder';
import { createEventsClient } from '@/lib/crm/core/EventsClient';
import { usePropertySchema } from '@/lib/crm/hooks/usePropertySchema';
import { useSlideoutStack } from '@/lib/crm/hooks/useSlideoutStack';
import {
  crmObjectListSentinelQuery,
  useStoreBackedList,
} from '@/lib/crm/store';
import { logger } from '@/lib/logger';

import { AutomationListItem } from './automation-list-item';

// ============================================================================
// Constants
// ============================================================================

// Seeds the builder composer. Plain text, not an `<Entity>` mention token —
// there is no automation to reference yet.
const CREATE_AUTOMATION_PROMPT = 'I want to create a CRM automation';

/**
 * Feature flag that reveals "internal" automations. Internal automations are
 * hidden from users by default — both in the add-automation library and in the
 * active-automations list — so they cannot be added or toggled off. Staff orgs
 * with this flag active can still see and manage them.
 */
const INTERNAL_AUTOMATIONS_FLAG = 'internal_automations';

const AUTOMATION_PROPERTIES = [
  'ahoy_display_name',
  'ahoy_enabled',
  'ahoy_summary',
  'ahoy_automation_name',
];

// AUTOMATION_TEMPLATES, WEBSITE_SCRIPT_NAMES and INTERNAL_SCRIPT_NAMES moved to
// `lib/crm/automations/templates.ts` when the automations list gained its own
// top-level page — the list config needs the same hidden-automation rules, and
// importing them from a component file breaks fast refresh.

// TemplateButton and AutomationLibrarySlideout moved to
// `lib/crm/automations/BuiltInAutomationLibrary.tsx`, shared with the
// top-level automations page. Two copies of the same create flow would have
// drifted, and the page could not reach this one.

// ============================================================================
// AutomationsContent
// ============================================================================

/**
 * The automations list itself, without page chrome.
 *
 * Exported because it now has two hosts: the settings section below (its
 * original home) and the top-level `/:orgId/automations` page. Both render the
 * same list — only the surrounding heading and width differ.
 */
export function AutomationsContent() {
  const { currentOrg } = useOrg();
  const orgId = currentOrg?.id;
  const { user } = useAuthInfo();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { openSlideout } = useSlideoutStack();
  const { hasPermission } = usePermissions();
  const { data: featureFlags } = useFeatureFlags(orgId);
  const isBuilderModeEnabled = useFeatureFlag('builder_mode');
  const canToggle = hasPermission(
    'ahoy_event_automation',
    'ahoy_property_updated'
  );
  const canCreate = hasPermission(
    'ahoy_event_automation',
    'ahoy_object_created'
  );

  const [isLibraryOpen, setIsLibraryOpen] = useState(false);

  const { schema } = usePropertySchema(orgId ?? 0, 'ahoy_event_automation');
  const summarySchema = schema?.['ahoy_summary'];

  const query = crmObjectListSentinelQuery(
    orgId ?? 0,
    'ahoy_event_automation',
    {
      properties: AUTOMATION_PROPERTIES,
    }
  );

  const listQuery = useQuery({
    ...query,
    enabled: !!orgId,
  });
  const { results, isLoading, error, refetch } = useStoreBackedList(listQuery);

  const activeFlags = useMemo(() => featureFlags ?? [], [featureFlags]);
  const showInternal = activeFlags.includes(INTERNAL_AUTOMATIONS_FLAG);

  const automations = useMemo(() => {
    const allAutomations = results;
    return allAutomations.filter(a => {
      const scriptName = a.properties?.['ahoy_automation_name']?.value;
      if (typeof scriptName !== 'string') {
        return true;
      }
      // Website automations are managed in the Websites settings section.
      if (WEBSITE_SCRIPT_NAMES.has(scriptName)) {
        return false;
      }
      // Internal automations are hidden unless the internal_automations flag is active,
      // so users cannot toggle them off from this section.
      if (!showInternal && INTERNAL_SCRIPT_NAMES.has(scriptName)) {
        return false;
      }
      return true;
    });
  }, [results, showInternal]);

  const toggleMutation = useMutation({
    mutationFn: async ({
      entityId,
      enabled,
      automationName,
    }: {
      entityId: string;
      enabled: string;
      automationName?: string;
    }) => {
      if (!orgId || !user) {
        throw new Error('Organization not available');
      }
      const automation = automations.find(a => a.entity_id === entityId);
      const startSeq = automation?.last_event_seq ?? 0;
      const builder = createEventBuilder({
        sourceId: String(user.id),
        startSeq,
      });
      builder.propertyUpdated(
        entityId,
        'ahoy_event_automation',
        'ahoy_enabled',
        enabled
      );
      const client = createEventsClient(orgId);
      await client.submitEvents(builder.build());

      // When re-enabling the reminder automation, trigger a backfill to
      // catch up any stale reminders created while the automation was off.
      // Shared with the slideout's toggle (reminderBackfill.ts).
      if (
        enabled === 'true' &&
        automationName === REMINDER_AUTOMATION_SCRIPT_NAME
      ) {
        await triggerReminderBackfill(orgId);
      }
    },
    onError: err => {
      logger.error(
        'Failed to update automation',
        err instanceof Error ? err : new Error(String(err)),
        { component: 'crm_events', org_id: orgId }
      );
      toast.error("We couldn't update the automation. Try again.");
      queryClient.invalidateQueries({ queryKey: query.queryKey, exact: false });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: query.queryKey, exact: false });
      queryClient.invalidateQueries({
        queryKey: ['crm', orgId, REMINDER_AUTOMATION_QUERY_KEY],
      });
      toast.success('Automation updated');
    },
  });

  const handleCreate = useCallback(() => {
    if (!orgId) {
      return;
    }
    if (isBuilderModeEnabled) {
      const { to, state } = buildCreateInBuilderNavState(
        orgId,
        'ahoy_event_automation',
        CREATE_AUTOMATION_PROMPT
      );
      navigate(to, { state });
      return;
    }
    // Flag off — fall back to the template library slideout.
    setIsLibraryOpen(true);
  }, [orgId, navigate, isBuilderModeEnabled]);

  const handleToggle = useCallback(
    (entityId: string, enabled: string, automationName?: string) => {
      toggleMutation.mutate({ entityId, enabled, automationName });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- .mutate is stable per React Query guarantees
    [toggleMutation.mutate]
  );

  const handleRowClick = useCallback(
    (entityId: string) => {
      openSlideout(entityId);
    },
    [openSlideout]
  );

  if (isLoading) {
    return (
      <div
        role='status'
        aria-live='polite'
        aria-label='Loading automations'
        className='space-y-3'
      >
        {[1, 2].map(i => (
          <div key={i} className='h-12 bg-bg-tertiary rounded animate-pulse' />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <QueryErrorState
        error={error}
        label="We couldn't load your automations"
        onRetry={refetch}
      />
    );
  }

  if (!orgId) {
    return null;
  }

  const hasExistingAutomation = automations.length > 0;

  return (
    <>
      {!hasExistingAutomation ? (
        <div
          role='status'
          aria-live='polite'
          className='flex flex-col items-center justify-center py-12 text-center'
        >
          <p className='text-sm text-fg-tertiary mb-4'>
            No automations yet. Add one to get started.
          </p>
          <Button
            size='sm'
            color='primary'
            iconLeading={Plus}
            onClick={handleCreate}
            isDisabled={!canCreate}
            disabledTooltip='Only admins have permission to edit automations'
          >
            Add
          </Button>
        </div>
      ) : (
        <div>
          <div className='flex justify-end mb-3'>
            <Button
              size='sm'
              color='secondary'
              iconLeading={Plus}
              onClick={handleCreate}
              isDisabled={!canCreate}
              disabledTooltip='Only admins have permission to edit automations'
            >
              Add
            </Button>
          </div>
          <ButtonList aria-label='Automations'>
            {automations.map(automation => (
              <AutomationListItem
                key={automation.entity_id}
                automation={automation}
                summarySchema={summarySchema}
                orgId={orgId}
                onToggle={handleToggle}
                onRowClick={handleRowClick}
                isToggling={toggleMutation.isPending}
                canToggle={canToggle}
              />
            ))}
          </ButtonList>
        </div>
      )}

      {/* Shared with the top-level automations page. This section used to
          carry its own copy — slideout, three create mutations and the
          webhook-secret modal — which would have drifted from the page's. */}
      <BuiltInAutomationLibrary
        isOpen={isLibraryOpen}
        onOpenChange={setIsLibraryOpen}
        orgId={orgId}
        activeFlags={activeFlags}
      />
    </>
  );
}

// ============================================================================
// AutomationsSection
// ============================================================================

export function AutomationsSection() {
  return (
    <div className='space-y-8'>
      <div>
        <h2 className='text-lg font-semibold text-fg-primary'>Automations</h2>
        <p className='text-sm text-fg-tertiary mt-0.5'>
          Manage automated workflows.
        </p>
      </div>
      <AutomationsContent />
    </div>
  );
}
